import type { HTMLAttributes, ReactNode, Ref } from 'react'
import { cx, isFilled } from '@/shared/lib'
import { Icon, type IconName } from '@/shared/ui/Icon'
import { Surface } from '@/shared/ui/Surface'
import s from './AppTopBar.module.css'

/**
 * Тон полосы под строкой шапки. Имена и палитра общие с `Banner`, `Alert`, `Toast`
 * и `StatusBadge`: `danger` — работа заблокирована, `warning` — идёт, но с последствием,
 * `success` — условие снято, `accent` — условие, о котором надо знать заранее.
 */
export type AppTopBarNoticeTone = 'accent' | 'success' | 'warning' | 'danger'

/** Что каркас отдаёт слоту `renderLogo`: раскладывается на свой элемент как есть. */
export type AppTopBarLogoSlotProps = {
  className: string
  children: ReactNode
}

/**
 * Ссылка «на главную» вокруг логотипа. `logoHref` и `renderLogo` вместе не собираются,
 * и каждый требует `logoLabel`: знак декоративен, а начертание ниже 900px не видно — без
 * подписи ссылка осталась бы безымянной. Union запрещает такую сборку компилятором,
 * а не подписью в контракте; тот же приём, что у `onDismiss`/`lockedNote` в `Banner`.
 */
type AppTopBarLogoProps =
  | {
      /** Адрес главной. Обычная `<a href>`: перезагрузит страницу, если это не роутер. */
      logoHref: string
      renderLogo?: never
      /** Начертание рядом со знаком. Обязательно вместе со ссылкой. */
      logoLabel: ReactNode
    }
  | {
      logoHref?: never
      /** Ссылка роутера: элемент создаёт потребитель — каркас о роутере не знает. */
      renderLogo: (props: AppTopBarLogoSlotProps) => ReactNode
      logoLabel: ReactNode
    }
  | {
      logoHref?: never
      renderLogo?: never
      /** Начертание рядом со знаком. Ниже 900px скрыто визуально, но не из дерева доступности. */
      logoLabel?: ReactNode
    }

type AppTopBarOwnProps = {
  /** Знак логотипа. Виден всегда, в том числе ниже 900px. Декоративен: имя даёт `logoLabel`. */
  logo?: ReactNode
  /**
   * Начертание скрыто визуально при любой ширине: в шапке только знак, а `logoLabel`
   * остаётся именем ссылки «на главную» для скринридера.
   */
  logoLabelHidden?: boolean
  /** Слот перед логотипом: кнопка вызова бокового меню на узком экране. Решение каркаса. */
  leading?: ReactNode
  /** Глобальный поиск: `SearchAutocomplete size="md"`. Ячейка растяжимая, лендмарк `search`. */
  search?: ReactNode
  /** Правый ряд: ценовой сегмент, корзина, колокол, аккаунт. Порядок задаёт потребитель. */
  actions?: ReactNode
  /**
   * Вторая строка под рядом: горизонтальное меню разделов (`AppNav`) с хвостом. Пусто —
   * строки нет. Заменяет боковую колонку каркаса.
   */
  nav?: ReactNode
  /** Текст полосы под строкой: состояние дебиторки. Пусто — полосы нет. */
  notice?: ReactNode
  noticeTone?: AppTopBarNoticeTone
  /** Действия полосы: «Акт сверки», «Оплатить». Переносятся на свою строку целиком. */
  noticeAction?: ReactNode
  /** Прилипание к верхнему краю. Выключается на печати и во вложенных раскладках. */
  sticky?: boolean
  /** Корневой `<header>`: измерение высоты каркасом, программная прокрутка. */
  ref?: Ref<HTMLElement>
}

export type AppTopBarProps = AppTopBarOwnProps &
  AppTopBarLogoProps &
  Omit<HTMLAttributes<HTMLElement>, 'children'>

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const NOTICE_TONE_CLASS: Record<AppTopBarNoticeTone, string | undefined> = {
  accent: s.noticeAccent,
  success: s.noticeSuccess,
  warning: s.noticeWarning,
  danger: s.noticeDanger,
}

/** Глиф плашки. `danger` и `warning` делят один знак: он про «внимание», а не про цвет. */
const NOTICE_ICON: Record<AppTopBarNoticeTone, IconName> = {
  accent: 'info',
  success: 'check',
  warning: 'warn',
  danger: 'warn',
}

/**
 * Верхняя полоса кабинета: логотип, глобальный поиск, ряд действий и полоса состояния
 * дебиторки под ними. Единственный элемент, видимый на каждом экране портала.
 *
 * Это **каркас**, а не набор функций: своих данных, состояния и запросов у него нет. Корзина,
 * ценовой сегмент, уведомления, аккаунт и текст полосы — домен, и приезжают слотами из
 * `features`/`entities`. Шапка владеет только раскладкой: прилипанием, слоем, переносом рядов,
 * схлопыванием логотипа до знака ниже 900px и геометрией полосы.
 *
 * Выпадашки в слотах строятся `Popover`/`Menu`, то есть порталом: у бара `z-index` и
 * `backdrop-filter`, значит собственный контекст наложения, и абсолютно позиционированное
 * меню из кита осталось бы внутри него — под содержимым страницы.
 *
 * Контракт: docs/ui-kit/contracts/app-topbar.md
 */
export function AppTopBar({
  logo,
  logoLabel,
  logoLabelHidden = false,
  logoHref,
  renderLogo,
  leading,
  search,
  actions,
  nav,
  notice,
  noticeTone = 'danger',
  noticeAction,
  sticky = true,
  className,
  ref,
  ...rest
}: AppTopBarProps) {
  // Пустым считается и boolean: идиома `can('cart.view') && <CartButton/>` из правила 8
  const hasLogo = isFilled(logo) || isFilled(logoLabel)
  const hasNotice = isFilled(notice)

  const brandContent = (
    <>
      {/* Знак декоративен: имя брендового блока несёт начертание, оно же имя ссылки */}
      {isFilled(logo) ? (
        <span className={s.logoMark} aria-hidden="true">
          {logo}
        </span>
      ) : null}

      {/* Ниже 900px (или всегда, по `logoLabelHidden`) начертание скрыто визуально, а не удалено:
          display: none оставил бы ссылку «на главную» без имени */}
      {isFilled(logoLabel) ? (
        <span className={logoLabelHidden ? s.logoLabelHidden : s.logoLabel}>{logoLabel}</span>
      ) : null}
    </>
  )

  const brandLinkClass = cx(s.brand, s.brandLink)

  const brand = !hasLogo ? null : renderLogo !== undefined ? (
    renderLogo({ className: brandLinkClass, children: brandContent })
  ) : logoHref !== undefined ? (
    <a className={brandLinkClass} href={logoHref}>
      {brandContent}
    </a>
  ) : (
    <span className={s.brand}>{brandContent}</span>
  )

  return (
    <Surface
      {...rest}
      ref={ref}
      as="header"
      variant="bar"
      className={cx(s.root, sticky && s.sticky, className)}
      data-sticky={sticky ? 'true' : 'false'}
    >
      <div className={s.row}>
        {isFilled(leading) ? <div className={s.leading}>{leading}</div> : null}

        {brand}

        {/* Лендмарк глобального поиска. Пустого лендмарка не бывает — обёртки тоже */}
        {isFilled(search) ? (
          <div className={s.search} role="search">
            {search}
          </div>
        ) : null}

        {isFilled(actions) ? <div className={s.actions}>{actions}</div> : null}
      </div>

      {isFilled(nav) ? <div className={s.navRow}>{nav}</div> : null}

      {hasNotice ? (
        <div className={cx(s.notice, NOTICE_TONE_CLASS[noticeTone])} data-tone={noticeTone}>
          {/* Плашка декоративна: она дублирует тон, а тон дублирует текст полосы */}
          <span className={s.noticeMarker} aria-hidden="true">
            <Icon name={NOTICE_ICON[noticeTone]} size="sm" />
          </span>

          <div className={s.noticeText}>{notice}</div>

          {isFilled(noticeAction) ? <div className={s.noticeRail}>{noticeAction}</div> : null}
        </div>
      ) : null}
    </Surface>
  )
}
